import * as THREE from 'three';
import { TrackElement } from './TrackElement';
import { TrainingTrackElement, MovementPattern } from '../../../../../shared/src/trainingTrack.types';
import { TRAINING_TRACK_CONFIG } from '../config/TrainingTrackConfig';

/**
 * Class representing a target element in the training track
 */
export class TargetElement extends TrackElement {
    private targetMesh: THREE.Mesh | null = null;
    private healthBarGroup: THREE.Group | null = null;
    private healthBar: THREE.Mesh | null = null;
    private startPosition: THREE.Vector3;
    private health: number;
    private maxHealth: number;
    private targetType: string;
    private movementPattern: string | undefined;
    private movementSpeed: number;
    private randomDir: THREE.Vector3 | null = null;
    
    /**
     * Constructor
     * @param element The target element data
     */
    constructor(element: TrainingTrackElement) {
        super(element);
        this.startPosition = element.position.clone();
        this.properties.health = element.properties?.health || 100;
        this.health = this.properties.health;
        this.maxHealth = this.health;
        this.targetType = element.properties?.targetType || 'STATIC';
        this.movementPattern = element.properties?.movementPattern;
        this.movementSpeed = element.properties?.movementSpeed || 1.0;
        this.properties.destructible = element.properties?.destructible;
    }
    
    /**
     * Creates the Three.js object for this target
     * @returns The created target object
     */
    protected createObject(): THREE.Object3D {
        // Choose color based on target type
        let color;
        switch (this.targetType) {
            case 'MOVING':
                color = TRAINING_TRACK_CONFIG.TARGET.MOVING_COLOR;
                break;
            case 'CASCADING':
                color = TRAINING_TRACK_CONFIG.TARGET.CASCADING_COLOR;
                break;
            default:
                color = TRAINING_TRACK_CONFIG.TARGET.STATIC_COLOR;
        }
        
        // Create target mesh
        const geometry = new THREE.BoxGeometry(1, 1, 1);
        const material = new THREE.MeshStandardMaterial({ color });
        this.targetMesh = new THREE.Mesh(geometry, material);
        this.targetMesh.scale.set(this.element.scale, this.element.scale, this.element.scale);
        this.targetMesh.userData.health = this.health;
        this.targetMesh.userData.maxHealth = this.maxHealth;
        this.targetMesh.castShadow = true;
        this.targetMesh.receiveShadow = true;
        
        // Create health bar
        this.healthBarGroup = new THREE.Group();
        
        // Background bar
        const bgGeometry = new THREE.PlaneGeometry(
            TRAINING_TRACK_CONFIG.TARGET.HEALTH_BAR_WIDTH,
            TRAINING_TRACK_CONFIG.TARGET.HEALTH_BAR_HEIGHT
        );
        const bgMaterial = new THREE.MeshBasicMaterial({
            color: TRAINING_TRACK_CONFIG.TARGET.HEALTH_BAR_BACKGROUND,
            side: THREE.DoubleSide
        });
        const bgBar = new THREE.Mesh(bgGeometry, bgMaterial);
        
        // Health bar
        const healthGeometry = new THREE.PlaneGeometry(
            TRAINING_TRACK_CONFIG.TARGET.HEALTH_BAR_WIDTH,
            TRAINING_TRACK_CONFIG.TARGET.HEALTH_BAR_HEIGHT
        );
        const healthMaterial = new THREE.MeshBasicMaterial({
            color: 0x00ff00,
            side: THREE.DoubleSide
        });
        this.healthBar = new THREE.Mesh(healthGeometry, healthMaterial);
        this.healthBar.scale.set(1, 1, 1); // Full health initially
        
        // Position health bar above target
        this.healthBarGroup.position.y = this.element.scale * 1.2;
        this.healthBarGroup.add(bgBar);
        this.healthBarGroup.add(this.healthBar);
        
        // Create a group to hold both the target and its health bar
        const group = new THREE.Group();
        group.add(this.targetMesh);
        group.add(this.healthBarGroup);
        
        return group;
    }
    
    /**
     * Updates the target animation and movement
     * @param deltaTime Time elapsed since last update in seconds
     * @param camera Optional camera for health bar to face
     */
    public update(deltaTime: number, camera?: THREE.Camera): void {
        if (!this.targetMesh || !this.healthBarGroup) return;
        
        // Only update movement for moving targets
        if (this.targetType === 'MOVING' && this.movementPattern) {
            // Update target position based on movement pattern
            switch (this.movementPattern) {
                case MovementPattern.LINEAR:
                    // Simple back and forth movement
                    const time = Date.now() * 0.001 * this.movementSpeed;
                    this.targetMesh.position.x = Math.sin(time) * 3;
                    break;
                case MovementPattern.CIRCULAR:
                    // Circular movement
                    const circleTime = Date.now() * 0.001 * this.movementSpeed;
                    this.targetMesh.position.x = Math.sin(circleTime) * 3;
                    this.targetMesh.position.z = Math.cos(circleTime) * 3;
                    break;
                case MovementPattern.RANDOM:
                    // Random movement (change direction occasionally)
                    if (Math.random() < 0.01) {
                        this.randomDir = new THREE.Vector3(
                            (Math.random() - 0.5) * 2,
                            0,
                            (Math.random() - 0.5) * 2
                        ).normalize();
                    }
                    
                    if (this.randomDir) {
                        this.targetMesh.position.x += this.randomDir.x * deltaTime * this.movementSpeed;
                        this.targetMesh.position.z += this.randomDir.z * deltaTime * this.movementSpeed;
                        
                        // Keep within bounds
                        const maxDist = 5;
                        if (this.targetMesh.position.distanceTo(this.startPosition) > maxDist) {
                            this.randomDir.negate();
                        }
                    }
                    break;
                case MovementPattern.ZIGZAG:
                    // Zigzag movement
                    const zigzagTime = Date.now() * 0.001 * this.movementSpeed;
                    this.targetMesh.position.x = Math.sin(zigzagTime * 2) * 3;
                    this.targetMesh.position.z = Math.sin(zigzagTime) * 3;
                    break;
            }
            
            // Update health bar position to follow target
            this.healthBarGroup.position.copy(this.targetMesh.position);
            this.healthBarGroup.position.y = this.element.scale * 1.2;
        }
        
        // Make health bar face the camera
        if (camera && this.healthBarGroup) {
            this.healthBarGroup.lookAt(camera.position);
        }
    }
    
    /**
     * Applies damage to the target
     * @param damage Amount of damage to apply
     * @returns True if the target was destroyed, false otherwise
     */
    public applyDamage(damage: number): boolean {
        if (!this.targetMesh || !this.healthBar) return false;
        
        // Check if target is destructible
        if (this.properties.destructible === false) {
            console.log(`[Target] ${this.id} is indestructible, ignoring damage`);
            return false;
        }
        
        // Apply damage to target
        this.health -= damage;
        this.properties.health = this.health;
        this.targetMesh.userData.health = this.health;
        
        // Update health bar
        const healthPercent = Math.max(0, this.health / this.maxHealth);
        this.healthBar.scale.x = healthPercent;
        
        // Change color based on health percentage
        if (healthPercent < 0.3) {
            (this.healthBar.material as THREE.MeshBasicMaterial).color.setHex(0xff0000); // Red for low health
        } else if (healthPercent < 0.6) {
            (this.healthBar.material as THREE.MeshBasicMaterial).color.setHex(0xffff00); // Yellow for medium health
        }
        
        // Handle destruction if health is depleted
        if (this.health <= 0) {
            this.handleDestruction();
            return true;
        }
        
        return false;
    }
    
    /**
     * Gets the current health of the target
     * @returns The current health
     */
    public getHealth(): number {
        return this.health;
    }
    
    /**
     * Gets the maximum health of the target
     * @returns The maximum health
     */
    public getMaxHealth(): number {
        return this.maxHealth;
    }
    
    /**
     * Gets the target type
     * @returns The target type
     */
    public getTargetType(): string {
        return this.targetType;
    }
    
    /**
     * Checks if this target is a cascading target
     * @returns True if this is a cascading target, false otherwise
     */
    public isCascading(): boolean {
        return this.properties.isCascading === true;
    }
    
    /**
     * Checks if this target can be affected by cascading explosions
     * @returns True if this target can be affected by cascading explosions, false otherwise
     */
    public isCascadable(): boolean {
        return this.properties.isCascadable !== false; // Default to true unless explicitly set to false
    }
    
    /**
     * Updates the health bar visual representation
     */
    private updateHealthBar(): void {
        if (!this.healthBar) return;
        
        const healthPercent = Math.max(0, this.properties.health / this.maxHealth);
        this.healthBar.scale.x = healthPercent;
        
        // Change color based on health percentage
        if (healthPercent < 0.3) {
            (this.healthBar.material as THREE.MeshBasicMaterial).color.setHex(0xff0000); // Red for low health
        } else if (healthPercent < 0.6) {
            (this.healthBar.material as THREE.MeshBasicMaterial).color.setHex(0xffff00); // Yellow for medium health
        } else {
            (this.healthBar.material as THREE.MeshBasicMaterial).color.setHex(0x00ff00); // Green for high health
        }
    }
    
    /**
     * Handles the destruction of the target
     */
    private handleDestruction(): void {
        if (!this.targetMesh || !this.healthBarGroup) return;
        
        // Hide the target and health bar
        this.targetMesh.visible = false;
        this.healthBarGroup.visible = false;
        
        // Optional: Add destruction effects here (e.g., particle effects)
        
        // Remove from scene after a delay
        setTimeout(() => {
            if (this.object) {
                const scene = this.object.parent;
                if (scene) {
                    this.remove(scene as THREE.Scene);
                }
            }
        }, 1000); // 1 second delay
    }
    
    public handleDamage(damage: number): void {
        if (!this.properties.health) return;
    
        console.log(`[Target] ${this.id} received damage:`, {
            currentHealth: this.properties.health,
            incomingDamage: damage,
            targetType: this.properties.targetType,
            isDestructible: this.properties.destructible !== false
        });
    
        if (this.properties.destructible === false) {
            console.log(`[Target] ${this.id} is indestructible, ignoring damage`);
            return;
        }
    
        this.properties.health -= damage;
        
        // Update health bar
        this.updateHealthBar();
    
        console.log(`[Target] ${this.id} health after damage:`, {
            remainingHealth: this.properties.health,
            wasDestroyed: this.properties.health <= 0
        });
    
        if (this.properties.health <= 0) {
            this.handleDestruction();
        }
    }
}